import { Component } from '@theme/component';

/**
 * AM / PM routine tabs.
 *
 * Only rendered when both routines have content — one routine is shown on its own, with no
 * component around it — so this can assume there is always more than one tab.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} tabs - The tab buttons, in AM then PM order.
 * @property {HTMLElement[]} panels - The panels, matching the tab order.
 *
 * @extends {Component<Refs>}
 */
class ConcernRoutine extends Component {
  requiredRefs = ['tabs', 'panels'];

  /** @type {AbortController | null} */
  #listeners = null;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners?.abort();
    const { signal } = (this.#listeners = new AbortController());

    this.addEventListener('keydown', this.#onKeydown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#listeners?.abort();
    this.#listeners = null;
  }

  /**
   * Switches to the clicked tab. Wired up declaratively with `on:click="/select"`.
   *
   * @param {Event} event
   */
  select(event) {
    const tab = /** @type {HTMLElement | null} */ (event.target)?.closest?.('[role="tab"]');
    if (!(tab instanceof HTMLElement)) return;

    this.#activate(this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (tab)));
  }

  /** @returns {HTMLButtonElement[]} */
  get #tabs() {
    return this.refs.tabs ?? [];
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    return this.refs.panels ?? [];
  }

  /**
   * @param {number} index - Index to show. Out of range indices are ignored.
   * @param {boolean} [focus] - Whether to move focus onto the newly selected tab.
   */
  #activate(index, focus = false) {
    const tabs = this.#tabs;
    const panels = this.#panels;
    const target = tabs[index];

    if (!target) return;

    for (const [position, tab] of tabs.entries()) {
      const active = position === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    }

    for (const [position, panel] of panels.entries()) {
      panel.hidden = position !== index;
    }

    // Drives the AM / PM tone shift from one place.
    this.dataset.active = target.dataset.routine ?? '';

    if (focus) target.focus();
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tab = /** @type {HTMLElement | null} */ (event.target)?.closest?.('[role="tab"]');
    if (!(tab instanceof HTMLElement)) return;

    const tabs = this.#tabs;
    const from = tabs.indexOf(/** @type {HTMLButtonElement} */ (tab));
    if (from === -1) return;

    let next;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (from + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (from - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next, true);
  };
}

if (!customElements.get('concern-routine-component')) {
  customElements.define('concern-routine-component', ConcernRoutine);
}
